import React from "react";
import { useGame } from "../game/em2c-store";
import { EM2C_RESOURCES as RESOURCES } from "../game/em2c-store";
import { Rocket, X, Clock, Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins } from "lucide-react";

const RES_ICONS = { Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins };

const PAYLOADS = [
  { key: "metal", label: "Metal Crate", res: { metal: 80 }, desc: "Bulk structural alloy for building" },
  { key: "parts", label: "Machine Parts", res: { parts: 40 }, desc: "Precision components for advanced buildings" },
  { key: "polymers", label: "Polymer Pack", res: { polymers: 40 }, desc: "Composite sealing plastics" },
  { key: "food", label: "Food Pallet", res: { food: 80 }, desc: "Emergency ration crates" },
  { key: "mixed", label: "Mixed Supply", res: { metal: 40, parts: 20, polymers: 15 }, desc: "Balanced resupply pack" },
];

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function CargoModal() {
  const open = useGame((s) => s.cargoOpen);
  const close = useGame((s) => s.closeCargo);
  const cargo = useGame((s) => s.cargo);
  const requestCargo = useGame((s) => s.requestCargo);
  const cargoWaitSeconds = useGame((s) => s.cargoWaitSeconds);

  if (!open) return null;

  const nextWait = cargoWaitSeconds(cargo.requestCount);
  const active = cargo.active;
  const progress = active && cargo.total > 0 ? 1 - cargo.remaining / cargo.total : 0;

  return (
    <div
      className="absolute inset-0 z-40 pointer-events-auto flex items-center justify-center bg-black/70 backdrop-blur-sm"
      style={{ fontFamily: "Rajdhani, sans-serif" }}
      data-testid="cargo-modal"
    >
      <div className="bg-[#0A0C10] border border-white/15 w-[min(720px,94vw)]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Rocket size={18} className="text-[#E8A37E]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Earth-Funded Supply</div>
              <div className="text-lg font-semibold uppercase tracking-wide text-white">Cargo Bay</div>
            </div>
          </div>
          <button
            data-testid="btn-close-cargo"
            onClick={close}
            className="w-8 h-8 flex items-center justify-center border border-white/20 bg-white/5 hover:border-[#ff3366] hover:text-[#ff3366] text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4">
          {/* In-transit tracker */}
          {active ? (
            <div className="mb-4 border border-[#00e5ff]/40 bg-cyan-500/5 p-3" data-testid="cargo-in-transit">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Rocket size={14} className="text-[#00e5ff] animate-pulse" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-white">
                    {cargo.payload?.label} — in transit
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-mono text-cyan-400">
                  <Clock size={12} />
                  {fmtTime(cargo.remaining)}
                </div>
              </div>
              <div className="h-1 bg-white/5">
                <div
                  className="h-full bg-[#00e5ff] transition-[width] duration-300"
                  style={{ width: `${progress * 100}%`, boxShadow: "0 0 8px #00e5ff" }}
                />
              </div>
              <div className="mt-2 text-[10px] font-mono text-slate-400">
                A new request can be made only after this shipment arrives.
              </div>
            </div>
          ) : (
            <div className="mb-4 border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Next shipment ETA</div>
                <div className="text-sm font-mono text-white">
                  {fmtTime(nextWait)} <span className="text-slate-500">(game time)</span>
                </div>
              </div>
              <div className="text-[11px] font-mono text-slate-400 text-right">
                Requests so far: <span className="text-white">{cargo.requestCount}</span>
                <br />
                Wait times: 5 → 10 → 20 → 30 → 60 min
              </div>
            </div>
          )}

          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Select payload</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {PAYLOADS.map((p) => (
              <button
                key={p.key}
                data-testid={`cargo-payload-${p.key}`}
                disabled={active}
                onClick={() => requestCargo(p.key)}
                className={`text-left p-3 border transition-colors ${
                  active
                    ? "border-white/10 bg-white/[0.02] opacity-40 cursor-not-allowed"
                    : "border-white/15 bg-white/5 hover:border-[#E8A37E] hover:bg-[#D85C31]/10"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Rocket size={12} className="text-[#E8A37E]" />
                  <span className="text-sm font-semibold uppercase tracking-wide text-white">{p.label}</span>
                </div>
                <div className="text-[11px] font-mono text-slate-400 mb-2 leading-tight">{p.desc}</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(p.res).map(([k, v]) => {
                    const m = RESOURCES[k];
                    const Icon = m ? RES_ICONS[m.icon] : Rocket;
                    return (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 border bg-black/30 text-[10px] font-mono"
                        style={{ borderColor: (m?.color || "#fff") + "60", color: m?.color || "#fff" }}
                      >
                        <Icon size={10} />
                        +{v} {m?.label || k}
                      </span>
                    );
                  })}
                </div>
              </button>
            ))}
          </div>

          <div className="text-[10px] font-mono text-slate-500 mt-4 pt-3 border-t border-white/10">
            Funded by Earth. No credits required — but Earth's launch windows grow further apart with each request.
          </div>
        </div>
      </div>
    </div>
  );
}
